import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Archive, MapPin, User, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Yard } from '../../types/stock.types';
import { yardsApi } from '../../utils/stock-api';
import YardManagers from './components/YardManagers';

/**
 * Closed Yards — the read-only archive (STOCK-MENU.md §6, STOCK.md §10).
 *
 * Every closed yard keeps its full sub-tree and every record, photo, signature and audit
 * entry underneath it. Nothing here can be edited: no new orders, no status changes, no
 * member changes.
 *
 * Former members keep access on purpose — membership survives closure, which is how the
 * people who did the work can retrieve their own records in a dispute. The list is
 * already filtered server-side by visibleYardIds(), so this page renders whatever it is
 * given without re-deriving access.
 */
export default function ClosedYardsPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK);

    const [yards, setYards] = useState<Yard[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setYards(await yardsApi.list({ status: 'closed' }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load closed yards');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    if (!canRead) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-4xl">
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Archive size={22} className="text-muted-foreground" />Closed Yards
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Archived yards. Everything remains readable for record-keeping and dispute
                    resolution — nothing can be changed.
                </p>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
                </div>
            ) : yards.length === 0 ? (
                <div className="text-center text-muted-foreground py-10">
                    <p>No closed yards.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {yards.map((y) => (
                        <Card key={y.id}>
                            <CardContent className="p-4">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Warehouse size={16} className="text-muted-foreground shrink-0" />
                                            <span className="font-medium">{y.yardName}</span>
                                            <span className="font-mono text-xs text-muted-foreground">
                                                {y.yardCode}
                                            </span>
                                        </div>
                                        <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                                            <p className="flex items-center gap-1.5">
                                                <MapPin size={14} />{y.branch?.name ?? '–'}
                                            </p>
                                            <p className="flex items-center gap-1.5">
                                                <User size={14} /><YardManagers managers={y.managers} />
                                            </p>
                                            {y.closeReason && (
                                                <p className="text-xs pt-1">
                                                    Closed{y.closedBy?.name ? ` by ${y.closedBy.name}` : ''}
                                                    {y.closedAt ? ` on ${new Date(y.closedAt).toLocaleDateString()}` : ''}
                                                    {' — '}{y.closeReason}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <Button asChild variant="outline" size="sm">
                                            <Link to={`/stock/closed-yards/${y.id}/deliveries`}>Deliveries</Link>
                                        </Button>
                                        <Button asChild variant="outline" size="sm">
                                            <Link to={`/stock/closed-yards/${y.id}/returns`}>Returns</Link>
                                        </Button>
                                        <Button asChild variant="outline" size="sm">
                                            <Link to={`/stock/closed-yards/${y.id}/imports`}>Import Orders</Link>
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
